import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

const WORD_DELAY_MS = 200;
const LINE_GAP_MS = 1000;
const BEGIN_DELAY_MS = 600;
const WORD_FADE_DURATION = "1.4s";
const MITRAA_ANTICIPATION_MS = 600;
const POST_MITRAA_PAUSE_MS = 600;
const LOGO_FADE_DELAY_MS = 300;
const LOGO_FADE_DURATION = "1200ms";

const LINES = [
  { words: "The best learning happens".split(" "), className: "text-3xl sm:text-4xl font-bold text-white" },
  {
    words: "when a teacher knows exactly where each student stands".split(" "),
    className: "text-2xl sm:text-3xl font-semibold text-sky-300",
  },
  {
    words: "and each student gets exactly what they need next.".split(" "),
    className: "text-xl sm:text-2xl font-normal",
    style: { color: "rgba(176, 219, 255, 0.75)" },
  },
  { words: ["Mitraa", "makes", "that", "possible."], className: "text-3xl sm:text-4xl font-bold text-white" },
];

function buildSchedule() {
  const flat = [];
  LINES.forEach((line, li) => line.words.forEach((word, wi) => flat.push({ li, wi, word })));
  let cumulative = 0;
  const delays = flat.map(({ li, wi, word }) => {
    if (li > 0 && wi === 0) cumulative += LINE_GAP_MS;
    if (li === 3 && word === "Mitraa") cumulative += MITRAA_ANTICIPATION_MS;
    if (li === 3 && word === "makes") cumulative += POST_MITRAA_PAUSE_MS;
    const delay = cumulative;
    cumulative += WORD_DELAY_MS;
    return delay;
  });
  return { flat, delays, total: flat.length, lastDelay: delays[delays.length - 1] };
}

export default function TryWelcomeScreen({ onBegin }) {
  const { delays, total, lastDelay } = useMemo(buildSchedule, []);
  const [revealed, setRevealed] = useState(0);
  const [showBegin, setShowBegin] = useState(false);
  const [logoVisible, setLogoVisible] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    const logoTimer = setTimeout(() => setLogoVisible(true), LOGO_FADE_DELAY_MS);
    return () => clearTimeout(logoTimer);
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      setRevealed(total);
      setShowBegin(true);
      return;
    }
    const ids = delays.map((delay, i) => setTimeout(() => setRevealed(i + 1), delay));
    ids.push(setTimeout(() => setShowBegin(true), lastDelay + WORD_DELAY_MS + BEGIN_DELAY_MS));
    timers.current = ids;
    return () => ids.forEach(clearTimeout);
  }, [delays, total, lastDelay]);

  const handleSkip = () => {
    timers.current.forEach(clearTimeout);
    setRevealed(total);
    setShowBegin(true);
  };

  let wordCursor = 0;

  return (
    <div className="w-full max-w-2xl mx-auto text-center relative z-10" data-testid="try-welcome-screen">
      {revealed >= 1 && (
        <button
          type="button"
          onClick={handleSkip}
          data-testid="try-welcome-skip-btn"
          className="fade-up absolute top-0 right-0 sm:top-1 sm:right-1 text-sm text-slate-400 hover:text-slate-300 transition-colors"
        >
          Skip intro
        </button>
      )}

      <img
        src="/mitraa-logo-transparent.webp"
        alt="Mitraa"
        className={`w-14 h-14 sm:w-16 sm:h-16 object-contain mx-auto mb-10 transition-opacity ${
          logoVisible ? "opacity-100" : "opacity-0"
        }`}
        style={{ transitionDuration: LOGO_FADE_DURATION, filter: "brightness(0) invert(1)" }}
        data-testid="try-welcome-logo"
      />

      <div className="space-y-5 sm:space-y-6" data-testid="try-welcome-lines">
        {LINES.map((line, li) => {
          const startIdx = wordCursor;
          wordCursor += line.words.length;
          if (revealed <= startIdx) return null;
          const visibleWords = line.words.slice(0, revealed - startIdx);
          return (
            <div
              key={li}
              className="flex flex-wrap justify-center gap-x-2 gap-y-1"
              data-testid={`try-welcome-line-${li}`}
            >
              {visibleWords.map((word, wi) => {
                const isMitraaWord = li === 3 && word === "Mitraa";
                return (
                  <span
                    key={wi}
                    className={`fade-up inline-block ${line.className}`}
                    style={{
                      animationDuration: WORD_FADE_DURATION,
                      ...(isMitraaWord ? { color: "#7dd3fc" } : line.style),
                    }}
                    data-testid={`try-welcome-line-${li}-word-${wi}`}
                  >
                    {word}
                  </span>
                );
              })}
            </div>
          );
        })}
      </div>

      {showBegin && (
        <Button
          onClick={onBegin}
          data-testid="try-welcome-begin-btn"
          className="fade-up mt-12 px-10 py-4 rounded-2xl text-base font-semibold bg-sky-500 hover:bg-sky-600 text-white shadow-md hover:shadow-lg transition-all"
        >
          Let's Begin
        </Button>
      )}
    </div>
  );
}
